import { useState, useEffect } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import { api } from '../../utils/api';
import { formatINR, formatDate } from '../../utils/formatters';
import { Building2, Plus, X, Users, FileText, Target, ChevronRight } from 'lucide-react';

interface TenantRow {
  id: string;
  name: string;
  slug: string;
  domain: string | null;
  config: any;
  isActive: boolean;
  createdAt: string;
  usersCount: number;
  contactsCount: number;
  dealsCount: number;
  invoicesCount: number;
  revenue: number;
  lastLogin: string | null;
}

interface TenantDetail {
  tenant: any;
  users: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    isActive: boolean;
    lastLoginAt: string | null;
    role: { displayName: string };
  }[];
  stats: { contacts: number; deals: number; invoices: number; products: number; tickets: number };
  revenue: { totalInvoiced: number; totalCollected: number };
}

const ALL_MODULES = [
  'crm', 'sales', 'finance', 'accounting', 'projects',
  'warehouse', 'procurement', 'service-mgmt', 'hr', 'automation', 'analytics',
];

export function TenantManagementPage() {
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TenantDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/tenants')
      .then(res => setTenants(res.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!detailId) { setDetail(null); return; }
    setDetailLoading(true);
    api.get(`/admin/tenants/${detailId}/stats`)
      .then(res => setDetail(res.data.data))
      .catch(() => {})
      .finally(() => setDetailLoading(false));
  }, [detailId]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenant Management</h1>
          <p className="text-sm text-gray-500">Manage all AURA deployments</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
        >
          <Plus size={16} /> Add Tenant
        </button>
      </div>

      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-4 py-3 font-medium text-gray-500">Tenant</th>
                <th className="px-4 py-3 font-medium text-gray-500">Slug</th>
                <th className="px-4 py-3 font-medium text-gray-500">Status</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Users</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Contacts</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Deals</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Invoices</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Revenue</th>
                <th className="px-4 py-3 font-medium text-gray-500">Last Login</th>
                <th className="px-4 py-3 font-medium text-gray-500">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {tenants.map(t => (
                <tr
                  key={t.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setDetailId(t.id)}
                >
                  <td className="px-4 py-3 font-medium text-gray-900">{t.name}</td>
                  <td className="px-4 py-3 text-gray-600">{t.slug}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                      t.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {t.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">{t.usersCount}</td>
                  <td className="px-4 py-3 text-right">{t.contactsCount}</td>
                  <td className="px-4 py-3 text-right">{t.dealsCount}</td>
                  <td className="px-4 py-3 text-right">{t.invoicesCount}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatINR(t.revenue)}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {t.lastLogin ? formatDate(t.lastLogin) : '--'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(t.createdAt)}</td>
                  <td className="px-4 py-3">
                    <ChevronRight size={16} className="text-gray-400" />
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-gray-400">No tenants found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Tenant Detail Panel */}
      {detailId && (
        <Modal open onClose={() => setDetailId(null)} className="!max-w-3xl !rounded-2xl !bg-white">
          <div>
            <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200 sticky top-0 z-10 bg-white rounded-t-2xl">
              <h2 className="text-lg font-bold text-gray-900">
                {detail?.tenant?.name || 'Loading...'}
              </h2>
              <RequestCloseButton className="rounded p-1 hover:bg-gray-100">
                <X size={20} className="text-gray-500" />
              </RequestCloseButton>
            </div>
            <div className="p-6 pt-4">

            {detailLoading ? (
              <div className="flex h-32 items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
              </div>
            ) : detail ? (
              <div className="space-y-5">
                {/* Stats row */}
                <div className="grid grid-cols-5 gap-3">
                  {[
                    { icon: Users, label: 'Users', val: detail.users.length },
                    { icon: Users, label: 'Contacts', val: detail.stats.contacts },
                    { icon: Target, label: 'Deals', val: detail.stats.deals },
                    { icon: FileText, label: 'Invoices', val: detail.stats.invoices },
                    { icon: Building2, label: 'Products', val: detail.stats.products },
                  ].map(s => (
                    <div key={s.label} className="rounded-lg bg-gray-50 p-3 text-center">
                      <s.icon size={16} className="mx-auto mb-1 text-gray-400" />
                      <p className="text-lg font-bold text-gray-900">{s.val}</p>
                      <p className="text-[10px] text-gray-500">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Revenue */}
                <div className="rounded-lg border border-gray-100 p-4">
                  <h3 className="mb-2 text-sm font-medium text-gray-500">Revenue</h3>
                  <div className="flex gap-6">
                    <div>
                      <p className="text-xs text-gray-400">Total Invoiced</p>
                      <p className="text-lg font-bold text-gray-900">{formatINR(detail.revenue.totalInvoiced)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Total Collected</p>
                      <p className="text-lg font-bold text-emerald-600">{formatINR(detail.revenue.totalCollected)}</p>
                    </div>
                  </div>
                </div>

                {/* Users */}
                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-500">Users</h3>
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="pb-2 font-medium text-gray-400">Name</th>
                        <th className="pb-2 font-medium text-gray-400">Email</th>
                        <th className="pb-2 font-medium text-gray-400">Role</th>
                        <th className="pb-2 font-medium text-gray-400">Status</th>
                        <th className="pb-2 font-medium text-gray-400">Last Login</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {detail.users.map(u => (
                        <tr key={u.id}>
                          <td className="py-1.5 text-gray-700">{u.firstName} {u.lastName}</td>
                          <td className="py-1.5 text-gray-500">{u.email}</td>
                          <td className="py-1.5 text-gray-600">{u.role.displayName}</td>
                          <td className="py-1.5">
                            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                              u.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {u.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-1.5 text-gray-400">
                            {u.lastLoginAt ? formatDate(u.lastLoginAt) : 'Never'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Tenant Config */}
                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-500">Tenant Configuration</h3>
                  <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
                    <p><strong>Slug:</strong> {detail.tenant.slug}</p>
                    <p><strong>Domain:</strong> {detail.tenant.domain || `${detail.tenant.slug}.aura.ardhanex.com`}</p>
                    <p><strong>Active:</strong> {detail.tenant.isActive ? 'Yes' : 'No'}</p>
                    <p><strong>Created:</strong> {formatDate(detail.tenant.createdAt)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-400">Failed to load details.</p>
            )}
            </div>
          </div>
        </Modal>
      )}

      {/* Add Tenant Modal */}
      {showAdd && <AddTenantModal onClose={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddTenantModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({
    name: '', slug: '', adminEmail: '', adminPassword: '',
    enabledModules: [...ALL_MODULES],
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/admin/tenants', form);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create tenant');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleModule = (mod: string) => {
    setForm(prev => ({
      ...prev,
      enabledModules: prev.enabledModules.includes(mod)
        ? prev.enabledModules.filter(m => m !== mod)
        : [...prev.enabledModules, mod],
    }));
  };

  return (
    <Modal open onClose={onClose} className="!rounded-2xl !bg-white">
      <div>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200 sticky top-0 z-10 bg-white rounded-t-2xl">
          <h2 className="text-lg font-bold text-gray-900">Add New Tenant</h2>
          <RequestCloseButton className="rounded p-1 hover:bg-gray-100">
            <X size={20} className="text-gray-500" />
          </RequestCloseButton>
        </div>
        <div className="p-6 pt-4">

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tenant Name</label>
            <input
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Sun Stellar Industries"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Slug</label>
            <input
              required
              pattern="^[a-z0-9-]+$"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
              value={form.slug}
              onChange={e => setForm(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
              placeholder="sun-stellar"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Admin Email</label>
              <input
                type="email"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                value={form.adminEmail}
                onChange={e => setForm(p => ({ ...p, adminEmail: e.target.value }))}
                placeholder="admin@company.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Admin Password</label>
              <input
                type="password"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                value={form.adminPassword}
                onChange={e => setForm(p => ({ ...p, adminPassword: e.target.value }))}
                placeholder="Min 6 characters"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Enabled Modules</label>
            <div className="flex flex-wrap gap-2">
              {ALL_MODULES.map(mod => (
                <button
                  key={mod}
                  type="button"
                  onClick={() => toggleModule(mod)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                    form.enabledModules.includes(mod)
                      ? 'bg-[var(--saffron-50)] text-[var(--ink-0)]'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {mod}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <RequestCloseButton
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </RequestCloseButton>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Tenant'}
            </button>
          </div>
        </form>
        </div>
      </div>
    </Modal>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
